// ============================================================
// 21. ENHANCED SalesSummaryReportPage.tsx
// ============================================================
// src/features/reports/pages/SalesSummaryReportPage.tsx

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getSalesSummary, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    BarChart3, DollarSign, ShoppingCart, TrendingUp,
    Receipt, RotateCcw, TrendingDown, Percent,
    Clock, Calendar, Zap, Target
} from 'lucide-react';
import { motion } from 'framer-motion';

export function SalesSummaryReportPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const { data: branches } = useQuery({
        queryKey: ['branches-report-sales', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-sales-summary', tenantId, params],
        queryFn: () => getSalesSummary(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'sales_summary', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <ReportHeader
                title="Sales Summary"
                description="Comprehensive sales analytics"
                icon={BarChart3}
                iconGradient="from-emerald-500 to-teal-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data ? (
                    <EmptyState
                        icon={<BarChart3 className="w-10 h-10" />}
                        title="No Sales Data"
                        description="No sales data found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        {/* Summary Cards with Comparisons */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <SummaryCard
                                label="Total Revenue"
                                value={formatNaira(data.summary?.totalRevenue)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                                comparison={data.comparison ? {
                                    value: parseFloat(data.comparison.revenueChange),
                                    label: 'vs prev period',
                                    isPositive: parseFloat(data.comparison.revenueChange) > 0
                                } : undefined}
                            />
                            <SummaryCard
                                label="Total Orders"
                                value={formatNumber(data.summary?.totalSales)}
                                icon={ShoppingCart}
                                gradient="from-blue-500 to-indigo-600"
                                comparison={data.comparison ? {
                                    value: parseFloat(data.comparison.ordersChange),
                                    label: 'vs prev period',
                                    isPositive: parseFloat(data.comparison.ordersChange) > 0
                                } : undefined}
                            />
                            <SummaryCard
                                label="Avg Order Value"
                                value={formatNaira(data.summary?.averageOrderValue)}
                                icon={Target}
                                gradient="from-purple-500 to-violet-600"
                            />
                            <SummaryCard
                                label="Gross Profit"
                                value={formatNaira(data.summary?.grossProfit)}
                                icon={TrendingUp}
                                gradient="from-green-500 to-emerald-600"
                                subtitle={`${data.summary?.profitMargin}% margin`}
                            />
                            <SummaryCard
                                label="Tax Collected"
                                value={formatNaira(data.summary?.totalTax)}
                                icon={Receipt}
                                gradient="from-amber-500 to-orange-600"
                            />
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Total Discounts"
                                value={formatNaira(data.summary?.totalDiscount)}
                                icon={Percent}
                                gradient="from-violet-500 to-purple-600"
                            />
                            <SummaryCard
                                label="Total Voids"
                                value={formatNumber(data.summary?.voidCount)}
                                icon={RotateCcw}
                                gradient="from-rose-500 to-pink-600"
                                subtitle={formatNaira(data.summary?.totalVoided)}
                            />
                            <SummaryCard
                                label="Total Refunds"
                                value={formatNumber(data.summary?.refundCount)}
                                icon={TrendingDown}
                                gradient="from-orange-500 to-yellow-600"
                                subtitle={formatNaira(data.summary?.totalRefunded)}
                            />
                            <SummaryCard
                                label="Total Cost"
                                value={formatNaira(data.summary?.totalCost)}
                                icon={Zap}
                                gradient="from-slate-500 to-slate-600"
                            />
                        </div>

                        {/* Hourly Sales Heatmap */}
                        {data.hourlyBreakdown && (
                            <Card>
                                <CardContent className="p-5">
                                    <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-emerald-500" />
                                        Hourly Sales Distribution
                                    </h3>
                                    <div className="grid grid-cols-12 gap-1">
                                        {data.hourlyBreakdown.map((hour: any) => {
                                            const maxRevenue = Math.max(...data.hourlyBreakdown.map((h: any) => h.revenue || 0), 1);
                                            const intensity = hour.revenue / maxRevenue;

                                            return (
                                                <motion.div
                                                    key={hour.hour}
                                                    initial={{ opacity: 0, scale: 0.8 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    transition={{ delay: hour.hour * 0.01 }}
                                                    className="text-center p-2 rounded-lg"
                                                    style={{
                                                        backgroundColor: `rgba(16, 185, 129, ${0.1 + intensity * 0.9})`,
                                                    }}
                                                >
                                                    <p className="text-xs font-medium text-slate-700">
                                                        {hour.hour}:00
                                                    </p>
                                                    <p className="text-xs font-bold text-emerald-800 mt-1">
                                                        {formatNaira(hour.revenue)}
                                                    </p>
                                                    <p className="text-xs text-slate-500">
                                                        {hour.count} orders
                                                    </p>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Payment Breakdown */}
                        {data.paymentBreakdown && data.paymentBreakdown.length > 0 && (
                            <Card>
                                <CardContent className="p-5">
                                    <h3 className="text-sm font-semibold text-slate-800 mb-4">Payment Method Breakdown</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {data.paymentBreakdown.map((p: any) => (
                                            <motion.div
                                                key={p.method}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="bg-slate-50 rounded-xl p-4 text-center"
                                            >
                                                <p className="text-xs text-slate-400 uppercase tracking-wider capitalize">
                                                    {p.method.replace(/_/g, ' ')}
                                                </p>
                                                <p className="text-xl font-bold text-slate-900 mt-1">
                                                    {formatNaira(p.amount)}
                                                </p>
                                                <Badge variant="info" size="sm" className="mt-2">
                                                    {p.percentage}%
                                                </Badge>
                                            </motion.div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Daily Breakdown Table */}
                        {data.dailyBreakdown && data.dailyBreakdown.length > 0 && (
                            <Card className="overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                    <h3 className="text-sm font-semibold text-slate-800">Daily Breakdown</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                                                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Orders</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Avg Order</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Tax</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Discount</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cost</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Profit</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {data.dailyBreakdown.map((row: any, i: number) => (
                                                <tr key={i} className="hover:bg-slate-50">
                                                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                                        {new Date(row.date).toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-semibold text-slate-900">
                                                        {formatNaira(row.revenue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm">{row.count}</td>
                                                    <td className="px-6 py-4 text-right text-sm">
                                                        {formatNaira(row.count > 0 ? row.revenue / row.count : 0)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-blue-600">
                                                        {formatNaira(row.tax)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-violet-600">
                                                        {formatNaira(row.discount)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                        {formatNaira(row.cost)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold">
                                                        <span className={row.revenue - row.cost >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                                                            {formatNaira(row.revenue - row.cost)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}

                        {/* Recent Sales */}
                        {data.recentSales && data.recentSales.length > 0 && (
                            <Card className="overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                    <h3 className="text-sm font-semibold text-slate-800">Recent Transactions</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Order #</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Cashier</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Amount</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {data.recentSales.map((sale: any, i: number) => (
                                                <tr key={i} className="hover:bg-slate-50">
                                                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                                        {sale.orderNumber}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">{sale.cashierName}</td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                        {formatNaira(sale.grandTotal)}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">
                                                        {new Date(sale.createdAt).toLocaleString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}